import { useEffect, StrictMode } from 'react';
import { useLocation } from 'react-router-dom';
import Lenis from 'lenis';
import 'lenis/dist/lenis.css';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Navbar from './components/Navbar';
import SiteLoader from './components/SiteLoader';
import TransitionOutlet from './components/PageTransition';
import Footer from './sections/Footer';
import ChatBot from './components/ChatBot';
import Home from './pages/Home';
import ServicePage from './pages/services/ServicePage';
import ServicesIndex from './pages/ServicesIndex';
import ProjectsPage from './pages/Projects';
import ContactPage from './pages/Contact';
import TechStackPage from './pages/TechStackPage';
import TestimonialsPage from './pages/TestimonialsPage';

function App() {
  const location = useLocation();

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);
    // phones: the address bar showing/hiding must not re-measure every pinned section (causes jumps)
    ScrollTrigger.config({ ignoreMobileResize: true });
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return undefined;

    const lenis = new Lenis({
      // lerp follows the wheel every frame (feels direct); the old 1.1s eased glide felt like input lag
      lerp: 0.14,
      smoothWheel: true,
    });
    window.lenis = lenis;
    lenis.on('scroll', ScrollTrigger.update);
    const tick = (time) => lenis.raf(time * 1000);
    gsap.ticker.add(tick);
    gsap.ticker.lagSmoothing(0);

    return () => {
      gsap.ticker.remove(tick);
      lenis.destroy();
      delete window.lenis;
    };
  }, []);

  // cache: a service worker keeps scripts, fonts and pictures on the device, so repeat visits and
  // page changes load from disk instead of the network (production builds only)
  useEffect(() => {
    if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return undefined;
    const register = () => navigator.serviceWorker.register('/sw.js').catch(() => {});
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1500));
    if (document.readyState === 'complete') idle(register);
    else window.addEventListener('load', () => idle(register), { once: true });
    return undefined;
  }, []);

  // pause decorative CSS animations in sections that are off screen (phones keep repainting them otherwise)
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    let io;
    const t = setTimeout(() => {
      io = new IntersectionObserver(
        (entries) => entries.forEach((e) => e.target.classList.toggle('is-off', !e.isIntersecting)),
        { rootMargin: '200px 0px' }
      );
      document.querySelectorAll('main section, main header, footer').forEach((el) => io.observe(el));
    }, 900); // after the page transition has swapped the new page in
    return () => {
      clearTimeout(t);
      io?.disconnect();
    };
  }, [location.pathname]);

  // new pages jump to the top inside TransitionOutlet, while the transition panels cover the screen
  useEffect(() => {
    requestAnimationFrame(() => ScrollTrigger.refresh());
  }, [location.hash]);

  return (
    <StrictMode>
        <div className="relative min-h-screen bg-base text-ink">
          <a
            href="#main"
            className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:rounded-full focus:bg-accent focus:px-5 focus:py-3 focus:text-accent-ink"
          >
            Skip to content
          </a>
          <SiteLoader />
          <Navbar />
          <main id="main" className="relative">
            <TransitionOutlet />
          </main>
          <Footer />
          <ChatBot />
        </div>
    </StrictMode>
  );
}

export const routes = [
  {
    path: '/',
    element: <App />,
    children: [
      { index: true, element: <Home /> },
      { path: 'services', element: <ServicesIndex /> },
      { path: 'services/:slug', element: <ServicePage /> },
      { path: 'projects', element: <ProjectsPage /> },
      { path: 'tech-stack', element: <TechStackPage /> },
      { path: 'testimonials', element: <TestimonialsPage /> },
      // rarely visited pages load on demand (the transition panels cover the load)
      { path: 'blog', lazy: async () => ({ Component: (await import('./pages/blog/BlogArchive')).default }) },
      { path: 'blog/:slug', lazy: async () => ({ Component: (await import('./pages/blog/BlogPost')).default }) },
      { path: 'contact', element: <ContactPage /> },
      { path: 'invoice', lazy: async () => ({ Component: (await import('./pages/InvoiceTemplate')).default }) },
    ],
  },
];

export default App;
